import { useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { usePostHog } from 'posthog-js/react';
import { campaignDraftStateAtom, campaignIsDirtyAtom, type CampaignDraft } from '@/store';
import { useCampaignMutationOptions, useCampaignBudgetMutationOptions, useUpdateCalloutMutationOptions, useUpdateSiteLinkMutationOptions } from '@/lib/query-options';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { useInvalidateQuery } from '@/hooks/useInvalidateQuery';
import { useToast } from '@/hooks/useToast';
import { addExcludedLocation, removeExcludedLocation } from '@/lib/apis';
import type { CampaignData, CampaignUpdate, DriftChange, ExcludedLocation } from '@/lib/types';
import { APPROVAL_STATE, CampaignDriftError, GoogleAdsConnectionBrokenError } from '@/lib/types';

/**
 * Hook for the parent component (campaign page) that initializes and manages the draft state.
 * This should only be called once per campaign page.
 *
 * ``excludedLocations`` (the current negative-location set, from its own query) is staged into the
 * same draft so editing exclusions is gated behind Save / the unsaved-changes guard like everything
 * else, instead of mutating immediately.
 */
export function useCampaignDraft(campaign: CampaignData | null, excludedLocations?: ExcludedLocation[]) {
  const [draftState, setDraftState] = useAtom(campaignDraftStateAtom);
  const isDirty = useAtomValue(campaignIsDirtyAtom);
  const posthog = usePostHog();
  const { toast } = useToast();
  const refetchCampaign = useRefetchCampaign(campaign?.slug || '');
  const invalidateQuery = useInvalidateQuery();

  const campaignSettingsMutation = useCampaignMutationOptions();
  const campaignBudgetMutation = useCampaignBudgetMutationOptions();
  const calloutUpdateMutation = useUpdateCalloutMutationOptions();
  const siteLinkUpdateMutation = useUpdateSiteLinkMutationOptions();

  // Initialize draft from campaign data
  useEffect(() => {
    if (campaign) {
      const initialDraft: CampaignDraft = {
        campaignId: campaign.id,
        name: campaign.name || '',
        businessDescription: campaign.business_desc || '',
        locations: campaign.target_locations || [],
        budget: campaign.budget || 0,
        targetCpa: campaign.target_cpa || 0,
        targetRoas: campaign.target_roas,
        biddingStrategyType: campaign.bidding_strategy_type || 'MAXIMIZE_CONVERSIONS',
        callouts: campaign.callouts || [],
        siteLinks: campaign.site_links || [],
      };

      // Only initialize if this is a different campaign or no draft exists
      if (!draftState.original || draftState.original.campaignId !== campaign.id) {
        setDraftState({
          draft: initialDraft,
          original: initialDraft,
        });
      }
    }
  }, [campaign?.id, draftState.original, setDraftState]);

  // Sync draft name when backend updates it (e.g., AI-generated name replaces placeholder).
  // Only update if the user hasn't manually edited the name (draft name still matches original).
  useEffect(() => {
    if (
      campaign &&
      draftState.original &&
      draftState.draft &&
      draftState.original.campaignId === campaign.id &&
      campaign.name !== draftState.original.name &&
      draftState.draft.name === draftState.original.name
    ) {
      setDraftState((prev) => ({
        draft: prev.draft ? { ...prev.draft, name: campaign.name } : prev.draft,
        original: prev.original ? { ...prev.original, name: campaign.name } : prev.original,
      }));
    }
  }, [campaign?.name, campaign?.id, draftState.original, draftState.draft, setDraftState]);

  // Sync draft description when the backend fills it in late — generation can land minutes
  // after creation, past the creation flow's delayed refetches (#6285). Same defensive
  // pattern as the name sync above: only while the user hasn't edited the field.
  useEffect(() => {
    const incoming = campaign?.business_desc || '';
    if (
      campaign &&
      draftState.original &&
      draftState.draft &&
      draftState.original.campaignId === campaign.id &&
      incoming !== draftState.original.businessDescription &&
      draftState.draft.businessDescription === draftState.original.businessDescription
    ) {
      setDraftState((prev) => ({
        draft: prev.draft ? { ...prev.draft, businessDescription: incoming } : prev.draft,
        original: prev.original ? { ...prev.original, businessDescription: incoming } : prev.original,
      }));
    }
  }, [campaign?.business_desc, campaign?.id, draftState.original, draftState.draft, setDraftState]);

  // Sync draft budget when backend updates it (e.g., budget recommendation).
  // Only update if the user hasn't manually edited the budget (draft budget still matches original).
  useEffect(() => {
    if (
      campaign &&
      draftState.original &&
      draftState.draft &&
      draftState.original.campaignId === campaign.id &&
      campaign.budget !== draftState.original.budget &&
      draftState.draft.budget === draftState.original.budget
    ) {
      setDraftState((prev) => ({
        draft: prev.draft ? { ...prev.draft, budget: campaign.budget } : prev.draft,
        original: prev.original ? { ...prev.original, budget: campaign.budget } : prev.original,
      }));
    }
  }, [campaign?.budget, campaign?.id, draftState.original, draftState.draft, setDraftState]);

  // Sync draft locations when the backend's list moves — Celery-deferred linking on stub
  // campaigns (#4317: HiPo / ICP / Location / Intent rows have no ``target_locations`` at POST
  // time) and reverse-sync imports of locations added in Google Ads (#5867). Same defensive
  // pattern as the name and budget syncs above: only re-seed while the user hasn't diverged
  // (draft still matches original by location identity), so pending edits are never clobbered.
  // The previous guard required both lists to be EMPTY, so a campaign that already had
  // locations never picked up server-side changes until a full page refresh remounted the draft.
  useEffect(() => {
    if (
      !campaign ||
      !draftState.original ||
      !draftState.draft ||
      draftState.original.campaignId !== campaign.id
    ) {
      return;
    }
    const locationKey = (locations: { criteria_id?: string | number | null }[]) =>
      JSON.stringify(locations.map((l) => String(l.criteria_id ?? '')).sort());
    const serverLocations = campaign.target_locations || [];
    const serverKey = locationKey(serverLocations);
    const originalKey = locationKey(draftState.original.locations);
    if (serverKey === originalKey) return; // backend hasn't moved
    if (locationKey(draftState.draft.locations) !== originalKey) return; // user has pending edits
    setDraftState((prev) => ({
      draft: prev.draft ? { ...prev.draft, locations: serverLocations } : prev.draft,
      original: prev.original ? { ...prev.original, locations: serverLocations } : prev.original,
    }));
  }, [campaign?.target_locations, campaign?.id, draftState.original, draftState.draft, setDraftState]);

  // Hydrate (and keep synced) the excluded-locations baseline from its query. Only while the user
  // hasn't diverged — pending edits are never clobbered — so it behaves like the other draft fields.
  useEffect(() => {
    if (excludedLocations === undefined) return;
    setDraftState((prev) => {
      if (!prev.draft || !prev.original) return prev;
      const untouched =
        prev.draft.excludedLocations === undefined ||
        JSON.stringify(prev.draft.excludedLocations) === JSON.stringify(prev.original.excludedLocations);
      if (!untouched) return prev;
      if (JSON.stringify(prev.original.excludedLocations) === JSON.stringify(excludedLocations)) return prev;
      return {
        draft: { ...prev.draft, excludedLocations },
        original: { ...prev.original, excludedLocations },
      };
    });
  }, [excludedLocations, setDraftState]);

  // Save all changes
  const saveChanges = useCallback(async () => {
    if (!campaign || !draftState.draft || !isDirty) return;

    try {
      const draft = draftState.draft;
      const original = draftState.original;

      // Whether anything OTHER than excluded locations changed. When only exclusions changed we skip
      // the settings PUT + campaign refetch (which reverse-syncs against live Google Ads and is slow
      // enough to time out) — exclusions persist via their own add/remove + query invalidation
      // below. Mirrors campaignIsDirtyAtom minus excludedLocations; errs toward true so a real
      // settings edit is never skipped (#5325 timeout fix).
      const settingsChanged =
        !original ||
        draft.name !== original.name ||
        draft.businessDescription !== original.businessDescription ||
        draft.budget !== original.budget ||
        draft.targetCpa !== original.targetCpa ||
        draft.targetRoas !== original.targetRoas ||
        draft.biddingStrategyType !== original.biddingStrategyType ||
        JSON.stringify(draft.locations) !== JSON.stringify(original.locations) ||
        JSON.stringify(draft.callouts) !== JSON.stringify(original.callouts) ||
        JSON.stringify(draft.siteLinks) !== JSON.stringify(original.siteLinks) ||
        JSON.stringify(draft.proximityTargets) !== JSON.stringify(original.proximityTargets);

      // Build the campaign update payload (budget and phone_numbers excluded — saved separately)
      const campaignUpdate = {
        ...campaign,
        budget: undefined,
        phone_numbers: undefined,
        name: draft.name,
        business_desc: draft.businessDescription,
        target_cpa: draft.targetCpa,
        target_locations: draft.locations,
        // The campaign's radius links (its subset of the business catalog). undefined
        // when untouched ⇒ JSON.stringify drops it ⇒ backend leaves the links unchanged;
        // a list (incl. []) is a full replace of the links.
        proximity_targets: draft.proximityTargets,

        target_roas: draft.biddingStrategyType === 'MAXIMIZE_CONVERSION_VALUE' ? draft.targetRoas : null,
        bidding_strategy_type: draft.biddingStrategyType,
        settings_state: APPROVAL_STATE.DRAFT,
        budget_state: APPROVAL_STATE.DRAFT,
      } satisfies CampaignUpdate;

      // Settings PUT first: it runs the drift guard against the pre-edit state and throws (409) on
      // GA drift, so we abort before persisting anything (and never mistake the user's edits for drift).
      if (settingsChanged) {
        await campaignSettingsMutation.mutateAsync(campaignUpdate);
      }

      // Guard passed — persist the field-level edits (budget saves via its own endpoint).
      if (draft.budget !== draftState.original?.budget) {
        await campaignBudgetMutation.mutateAsync({
          campaignId: campaign.id,
          budgetUpdate: { budget: draft.budget },
        });
      }

      // Save callouts if changed
      if (JSON.stringify(draft.callouts) !== JSON.stringify(draftState.original?.callouts)) {
        for (const callout of draft.callouts) {
          if (callout.id) {
            const originalCallout = draftState.original?.callouts.find(c => c.id === callout.id);
            if (originalCallout && originalCallout.text !== callout.text) {
              await calloutUpdateMutation.mutateAsync({ campaignId: campaign.id, callout });
            }
          } else if (callout.text.trim()) {
            await calloutUpdateMutation.mutateAsync({ campaignId: campaign.id, callout });
          }
        }
        invalidateQuery(['callouts', 'update']);
      }

      // Save sitelinks if changed
      if (JSON.stringify(draft.siteLinks) !== JSON.stringify(draftState.original?.siteLinks)) {
        for (const siteLink of draft.siteLinks) {
          if (siteLink.id) {
            const originalSiteLink = draftState.original?.siteLinks.find(sl => sl.id === siteLink.id);
            if (originalSiteLink && JSON.stringify(originalSiteLink) !== JSON.stringify(siteLink)) {
              await siteLinkUpdateMutation.mutateAsync({ campaignId: campaign.id, siteLink });
            }
          }
        }
        invalidateQuery(['siteLinks', 'update']);
      }

      // Save excluded-location changes (staged in the draft; persisted as add/remove diffs).
      if (
        draft.excludedLocations !== undefined &&
        JSON.stringify(draft.excludedLocations) !== JSON.stringify(draftState.original?.excludedLocations)
      ) {
        const originalExcluded = draftState.original?.excludedLocations ?? [];
        const draftIds = new Set(draft.excludedLocations.map((e) => e.criteria_id));
        const originalIds = new Set(originalExcluded.map((e) => e.criteria_id));
        for (const e of originalExcluded.filter((e) => !draftIds.has(e.criteria_id))) {
          await removeExcludedLocation(campaign.id, e.criteria_id);
        }
        for (const e of draft.excludedLocations.filter((e) => !originalIds.has(e.criteria_id))) {
          await addExcludedLocation(campaign.id, e.criteria_id);
        }
        invalidateQuery(['campaign-excluded-locations', campaign.id]);
      }

      // Track analytics
      posthog?.capture('campaign_settings_saved', {
        campaign_id: campaign.id,
        fields_changed: getChangedFields(draftState.original, draft),
      });

      // Invalidate and refetch so campaign data is fresh — only when settings actually changed; an
      // excluded-only save invalidates just the excluded query above and skips the slow campaign GET.
      if (settingsChanged) {
        invalidateQuery(['campaigns', 'update']);
        await refetchCampaign();
      }

      // Update original to match draft after refetch (no longer dirty)
      setDraftState((prev) => ({
        ...prev,
        original: prev.draft,
      }));
    } catch (error) {
      if (error instanceof GoogleAdsConnectionBrokenError) {
        toast({
          title: 'Google Ads Connection Lost',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      toast({
        description: error instanceof Error ? error.message : 'Failed to save changes',
        variant: 'destructive',
      });
    }
  }, [
    campaign,
    draftState,
    isDirty,
    campaignSettingsMutation,
    campaignBudgetMutation,
    calloutUpdateMutation,
    siteLinkUpdateMutation,
    posthog,
    invalidateQuery,
    refetchCampaign,
    setDraftState,
    toast,
  ]);

  // Discard changes
  const discardChanges = useCallback(() => {
    setDraftState((prev) => ({
      ...prev,
      draft: prev.original,
    }));
  }, [setDraftState]);

  return {
    draft: draftState.draft,
    isDirty,
    saveChanges,
    discardChanges,
    isSaving: campaignSettingsMutation.isPending || campaignBudgetMutation.isPending || calloutUpdateMutation.isPending || siteLinkUpdateMutation.isPending,
  };
}

/**
 * Hook for child components that need to read/update the draft state.
 * Does not initialize the draft - that's handled by useCampaignDraft in the parent.
 */
export function useCampaignDraftFields() {
  const [draftState, setDraftState] = useAtom(campaignDraftStateAtom);

  // Update a single field in the draft
  const updateDraft = useCallback(<K extends keyof CampaignDraft>(
    field: K,
    value: CampaignDraft[K]
  ) => {
    setDraftState((prev) => {
      if (!prev.draft) return prev;
      return {
        ...prev,
        draft: {
          ...prev.draft,
          [field]: value,
        },
      };
    });
  }, [setDraftState]);

  // Sync a field value without marking as dirty (updates both draft and original)
  const syncDraft = useCallback(<K extends keyof CampaignDraft>(
    field: K,
    value: CampaignDraft[K]
  ) => {
    setDraftState((prev) => {
      if (!prev.draft || !prev.original) return prev;
      return {
        draft: { ...prev.draft, [field]: value },
        original: { ...prev.original, [field]: value },
      };
    });
  }, [setDraftState]);

  return {
    draft: draftState.draft,
    updateDraft,
    syncDraft,
  };
}

/**
 * Hook for child components that need to check dirty state and trigger save.
 * Does not initialize the draft - that's handled by useCampaignDraft in the layout.
 * Use this to show "Save Changes" button in child pages.
 */
export function useCampaignDraftStatus(campaign: CampaignData | null) {
  const [draftState, setDraftState] = useAtom(campaignDraftStateAtom);
  const isDirty = useAtomValue(campaignIsDirtyAtom);
  const posthog = usePostHog();
  const { toast } = useToast();
  const refetchCampaign = useRefetchCampaign(campaign?.slug || '');
  const invalidateQuery = useInvalidateQuery();

  const campaignSettingsMutation = useCampaignMutationOptions();
  const campaignBudgetMutation = useCampaignBudgetMutationOptions();
  const calloutUpdateMutation = useUpdateCalloutMutationOptions();
  const siteLinkUpdateMutation = useUpdateSiteLinkMutationOptions();

  // Brief "Saved ✓" confirmation shown next to the Save Changes button after a
  // successful save. Auto-clears after 2s. Matches the reference pattern from
  // business/budget-v2.tsx and organizations/index.tsx — see #4387.
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Tracked locally rather than derived from `mutation.isPending` flags below
  // because saveChanges fires multiple mutations in sequence with non-mutation
  // work between them (refetch + setDraftState). The OR of pending flags
  // briefly drops to false between steps, which makes the Save Changes button
  // flicker spinner → text → tick. Holding our own boolean keeps the spinner
  // continuous from click to success/failure. #4387.
  const [isSavingLocal, setIsSavingLocal] = useState(false);

  // Drift guard (#reverse-sync): a PUT /campaigns/{id} can return 409 when Google Ads
  // drifted since the campaign was loaded. We surface the reported changes in a modal
  // so the user can reload and re-apply, rather than silently clobbering remote edits.
  const [driftChanges, setDriftChanges] = useState<DriftChange[]>([]);
  const [driftModalOpen, setDriftModalOpen] = useState(false);

  // Save all changes (same logic as useCampaignDraft)
  const saveChanges = useCallback(async () => {
    if (!campaign || !draftState.draft || !isDirty) return;

    setIsSavingLocal(true);
    try {
      const draft = draftState.draft;
      const original = draftState.original;

      // Whether anything OTHER than excluded locations changed. When only exclusions changed we skip
      // the settings PUT + campaign refetch (which reverse-syncs against live Google Ads and is slow
      // enough to time out) — exclusions persist via their own add/remove + query invalidation
      // below. Mirrors campaignIsDirtyAtom minus excludedLocations; errs toward true so a real
      // settings edit is never skipped (#5325 timeout fix).
      const settingsChanged =
        !original ||
        draft.name !== original.name ||
        draft.businessDescription !== original.businessDescription ||
        draft.budget !== original.budget ||
        draft.targetCpa !== original.targetCpa ||
        draft.targetRoas !== original.targetRoas ||
        draft.biddingStrategyType !== original.biddingStrategyType ||
        JSON.stringify(draft.locations) !== JSON.stringify(original.locations) ||
        JSON.stringify(draft.callouts) !== JSON.stringify(original.callouts) ||
        JSON.stringify(draft.siteLinks) !== JSON.stringify(original.siteLinks) ||
        JSON.stringify(draft.proximityTargets) !== JSON.stringify(original.proximityTargets);

      // Build the campaign update payload (budget and phone_numbers excluded — saved separately)
      const campaignUpdate = {
        ...campaign,
        budget: undefined,
        phone_numbers: undefined,
        name: draft.name,
        business_desc: draft.businessDescription,
        target_cpa: draft.targetCpa,
        target_locations: draft.locations,
        // The campaign's radius links (its subset of the business catalog). undefined
        // when untouched ⇒ JSON.stringify drops it ⇒ backend leaves the links unchanged;
        // a list (incl. []) is a full replace of the links.
        proximity_targets: draft.proximityTargets,

        target_roas: draft.biddingStrategyType === 'MAXIMIZE_CONVERSION_VALUE' ? draft.targetRoas : null,
        bidding_strategy_type: draft.biddingStrategyType,
        settings_state: APPROVAL_STATE.DRAFT,
        budget_state: APPROVAL_STATE.DRAFT,
      } satisfies CampaignUpdate;

      // Settings PUT first: it runs the drift guard against the pre-edit state and throws (409) on
      // GA drift, so we abort before persisting anything (and never mistake the user's edits for drift).
      if (settingsChanged) {
        await campaignSettingsMutation.mutateAsync(campaignUpdate);
      }

      // Guard passed — persist the field-level edits (budget saves via its own endpoint).
      if (draft.budget !== draftState.original?.budget) {
        await campaignBudgetMutation.mutateAsync({
          campaignId: campaign.id,
          budgetUpdate: { budget: draft.budget },
        });
      }

      // Save callouts if changed
      if (JSON.stringify(draft.callouts) !== JSON.stringify(draftState.original?.callouts)) {
        for (const callout of draft.callouts) {
          if (callout.id) {
            const originalCallout = draftState.original?.callouts.find(c => c.id === callout.id);
            if (originalCallout && originalCallout.text !== callout.text) {
              await calloutUpdateMutation.mutateAsync({ campaignId: campaign.id, callout });
            }
          } else if (callout.text.trim()) {
            await calloutUpdateMutation.mutateAsync({ campaignId: campaign.id, callout });
          }
        }
        invalidateQuery(['callouts', 'update']);
      }

      // Save sitelinks if changed
      if (JSON.stringify(draft.siteLinks) !== JSON.stringify(draftState.original?.siteLinks)) {
        for (const siteLink of draft.siteLinks) {
          if (siteLink.id) {
            const originalSiteLink = draftState.original?.siteLinks.find(sl => sl.id === siteLink.id);
            if (originalSiteLink && JSON.stringify(originalSiteLink) !== JSON.stringify(siteLink)) {
              await siteLinkUpdateMutation.mutateAsync({ campaignId: campaign.id, siteLink });
            }
          }
        }
        invalidateQuery(['siteLinks', 'update']);
      }

      // Save excluded-location changes (staged in the draft; persisted as add/remove diffs).
      if (
        draft.excludedLocations !== undefined &&
        JSON.stringify(draft.excludedLocations) !== JSON.stringify(draftState.original?.excludedLocations)
      ) {
        const originalExcluded = draftState.original?.excludedLocations ?? [];
        const draftIds = new Set(draft.excludedLocations.map((e) => e.criteria_id));
        const originalIds = new Set(originalExcluded.map((e) => e.criteria_id));
        for (const e of originalExcluded.filter((e) => !draftIds.has(e.criteria_id))) {
          await removeExcludedLocation(campaign.id, e.criteria_id);
        }
        for (const e of draft.excludedLocations.filter((e) => !originalIds.has(e.criteria_id))) {
          await addExcludedLocation(campaign.id, e.criteria_id);
        }
        invalidateQuery(['campaign-excluded-locations', campaign.id]);
      }

      // Track analytics
      posthog?.capture('campaign_settings_saved', {
        campaign_id: campaign.id,
        fields_changed: getChangedFields(draftState.original, draft),
      });

      // Invalidate and refetch so campaign data is fresh — only when settings actually changed; an
      // excluded-only save invalidates just the excluded query above and skips the slow campaign GET.
      if (settingsChanged) {
        invalidateQuery(['campaigns', 'update']);
        await refetchCampaign();
      }

      // Update original to match draft after refetch (no longer dirty)
      setDraftState((prev) => ({
        ...prev,
        original: prev.draft,
      }));
      setSaveSuccess(true);
      window.setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      if (error instanceof CampaignDriftError) {
        setDriftChanges(error.changes);
        setDriftModalOpen(true);
        return;
      }
      if (error instanceof GoogleAdsConnectionBrokenError) {
        toast({
          title: 'Google Ads Connection Lost',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      toast({
        description: error instanceof Error ? error.message : 'Failed to save changes',
        variant: 'destructive',
      });
    } finally {
      setIsSavingLocal(false);
    }
  }, [
    campaign,
    draftState,
    isDirty,
    campaignSettingsMutation,
    campaignBudgetMutation,
    calloutUpdateMutation,
    siteLinkUpdateMutation,
    posthog,
    invalidateQuery,
    refetchCampaign,
    setDraftState,
    toast,
  ]);

  // Discard changes
  const discardChanges = useCallback(() => {
    setDraftState((prev) => ({
      ...prev,
      draft: prev.original,
    }));
  }, [setDraftState]);

  return {
    isDirty,
    saveChanges,
    discardChanges,
    saveSuccess,
    driftChanges,
    driftModalOpen,
    setDriftModalOpen,
    // isSavingLocal covers the full saveChanges flow (incl. refetch + state
    // reconciliation between mutations). The mutation pending flags are kept
    // in the OR as belt-and-braces — if anything else triggers them outside
    // saveChanges, the button still reflects an in-flight save.
    isSaving:
      isSavingLocal ||
      campaignSettingsMutation.isPending ||
      campaignBudgetMutation.isPending ||
      calloutUpdateMutation.isPending ||
      siteLinkUpdateMutation.isPending,
  };
}

// Helper to get list of changed fields for analytics
function getChangedFields(
  original: CampaignDraft | null,
  draft: CampaignDraft
): string[] {
  if (!original) return [];

  const changed: string[] = [];
  if (draft.name !== original.name) changed.push('name');
  if (draft.businessDescription !== original.businessDescription) changed.push('businessDescription');

  if (draft.budget !== original.budget) changed.push('budget');
  if (draft.targetCpa !== original.targetCpa) changed.push('targetCpa');
  if (draft.targetRoas !== original.targetRoas) changed.push('targetRoas');
  if (draft.biddingStrategyType !== original.biddingStrategyType) changed.push('biddingStrategyType');
  if (JSON.stringify(draft.locations) !== JSON.stringify(original.locations)) changed.push('locations');
  if (JSON.stringify(draft.callouts) !== JSON.stringify(original.callouts)) changed.push('callouts');
  if (JSON.stringify(draft.siteLinks) !== JSON.stringify(original.siteLinks)) changed.push('siteLinks');
  if (JSON.stringify(draft.proximityTargets) !== JSON.stringify(original.proximityTargets)) changed.push('proximityTargets');

  return changed;
}
